import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import AdminLayout from "@/components/layout/AdminLayout";
import { fetchOverviewStats } from "@/api/dashboard";
import StatsCard from "@/components/dashboard/StatsCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Users,
  BookOpen,
  CreditCard,
  DollarSign,
  TrendingUp,
  BarChart2,
  UserPlus,
  Plus,
  ArrowRight,
  Eye,
  Calendar,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

// Helper function to format date and time
const formatDateTime = (date: Date) => {
  const formattedDate = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const formattedTime = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  const getTimeAgo = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    }

    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    }

    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    return `${diffMinutes} minute${diffMinutes > 1 ? "s" : ""} ago`;
  };

  const timeAgo = getTimeAgo(date);

  return {
    date: formattedDate,
    time: formattedTime,
    timeAgo: timeAgo,
    twoLine: `<div>${formattedDate}, ${formattedTime}</div><div class="text-xs opacity-80">${timeAgo}</div>`,
  };
};

const Dashboard = () => {
  const navigate = useNavigate();

  // Fetch overview data using the new focused API
  const { data: overviewData, isLoading } = useQuery({
    queryKey: ["overviewStats"],
    queryFn: fetchOverviewStats,
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
  });

  if (isLoading) {
    return (
      <AdminLayout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!overviewData) {
    return (
      <AdminLayout title="Dashboard">
        <div className="p-8 text-center">
          <h2 className="text-xl font-medium mb-2">
            Unable to load dashboard data
          </h2>
          <p className="text-muted-foreground">
            Please try refreshing the page.
          </p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Dashboard">
      <div className="space-y-8">
        {/* Simple Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
            <p className="text-muted-foreground mt-1">
              Overview of your platform's performance
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => navigate("/admin/books/new")}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Book
            </Button>
            <Button
              onClick={() => navigate("/admin/analytics/content")}
              className="flex items-center gap-2"
            >
              <BarChart2 className="h-4 w-4" />
              Analytics
            </Button>
          </div>
        </div>

        {/* Essential Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatsCard
            title="Total Users"
            value={overviewData.totalUsers}
            icon={<Users className="h-4 w-4" />}
            footer={
              <div className="text-xs text-muted-foreground mt-2">
                <span className="font-medium text-foreground">
                  {overviewData.newUsers}
                </span>{" "}
                new users in last 30 days
              </div>
            }
          />

          <StatsCard
            title="Active Subscriptions"
            value={overviewData.activeSubscriptions}
            icon={<CreditCard className="h-4 w-4" />}
            footer={
              <div className="text-xs text-muted-foreground mt-2">
                <span className="font-medium text-foreground">
                  {overviewData.totalUsers > 0
                    ? Math.round(
                        (overviewData.activeSubscriptions /
                          overviewData.totalUsers) *
                          100
                      )
                    : 0}
                  %
                </span>{" "}
                of total users
              </div>
            }
          />

          <StatsCard
            title="Books"
            value={overviewData.totalBooks}
            icon={<BookOpen className="h-4 w-4" />}
            footer={
              <div className="text-xs text-muted-foreground mt-2">
                <span className="font-medium text-foreground">
                  {overviewData.activeBooks}
                </span>{" "}
                active books
              </div>
            }
          />

          <StatsCard
            title="This Month Revenue"
            value={`₹${overviewData.estimatedRevenue.toFixed(2)}`}
            icon={<DollarSign className="h-4 w-4" />}
            footer={
              <div className="text-xs text-muted-foreground mt-2">
                <div className="flex items-center gap-1">
                  {overviewData.revenueChangePercentage >= 0 ? (
                    <span className="text-green-600 font-medium">
                      +{overviewData.revenueChangePercentage.toFixed(1)}%
                    </span>
                  ) : (
                    <span className="text-red-600 font-medium">
                      {overviewData.revenueChangePercentage.toFixed(1)}%
                    </span>
                  )}
                  <span>vs last month</span>
                </div>
                <div className="text-xs text-muted-foreground/80 mt-1">
                  Last month: ₹{overviewData.previousMonthRevenue.toFixed(2)}
                </div>
              </div>
            }
          />
        </div>

        {/* Quick Actions & Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center">
                <TrendingUp className="h-4 w-4 mr-2 text-primary" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Button
                  variant="outline"
                  className="h-20 flex flex-col items-center justify-center gap-2"
                  onClick={() => navigate("/admin/books/new")}
                >
                  <BookOpen className="h-5 w-5" />
                  <span className="text-sm">Add Book</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-20 flex flex-col items-center justify-center gap-2"
                  onClick={() => navigate("/admin/users/new")}
                >
                  <UserPlus className="h-5 w-5" />
                  <span className="text-sm">Add User</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-20 flex flex-col items-center justify-center gap-2"
                  onClick={() => navigate("/admin/collections/new")}
                >
                  <Plus className="h-5 w-5" />
                  <span className="text-sm">New Collection</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-20 flex flex-col items-center justify-center gap-2"
                  onClick={() => navigate("/admin/promotions/new")}
                >
                  <Calendar className="h-5 w-5" />
                  <span className="text-sm">Create Promotion</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Recent Users */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold flex items-center">
                <UserPlus className="h-4 w-4 mr-2 text-primary" />
                Recent Users
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/admin/analytics/users")}
                className="flex items-center gap-1"
              >
                View All
                <ArrowRight className="h-3 w-3" />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {overviewData.recentUsers.length > 0 ? (
                  overviewData.recentUsers.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center gap-3 p-3 hover:bg-muted/50 rounded-md border border-border"
                    >
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium text-sm">
                        {user.name.substring(0, 1).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-sm truncate">
                          {user.name}
                        </h3>
                        <p className="text-xs text-muted-foreground truncate">
                          {user.email}
                        </p>
                      </div>
                      <div className="text-xs text-muted-foreground whitespace-nowrap text-right">
                        <div
                          dangerouslySetInnerHTML={{
                            __html: formatDateTime(new Date(user.registered_at))
                              .twoLine,
                          }}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-muted-foreground text-sm py-4">
                    No recent users
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Analytics Navigation */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center">
              <BarChart2 className="h-4 w-4 mr-2 text-primary" />
              Detailed Analytics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Button
                variant="outline"
                className="h-24 flex flex-col items-center justify-center gap-2"
                onClick={() => navigate("/admin/analytics/content")}
              >
                <BookOpen className="h-6 w-6 text-primary" />
                <div className="text-center">
                  <div className="font-medium text-sm">Content Analytics</div>
                  <div className="text-xs text-muted-foreground">
                    Books, languages, categories
                  </div>
                </div>
              </Button>
              <Button
                variant="outline"
                className="h-24 flex flex-col items-center justify-center gap-2"
                onClick={() => navigate("/admin/analytics/users")}
              >
                <Users className="h-6 w-6 text-primary" />
                <div className="text-center">
                  <div className="font-medium text-sm">User Analytics</div>
                  <div className="text-xs text-muted-foreground">
                    Growth, registrations, activity
                  </div>
                </div>
              </Button>
              <Button
                variant="outline"
                className="h-24 flex flex-col items-center justify-center gap-2"
                onClick={() => navigate("/admin/analytics/business")}
              >
                <DollarSign className="h-6 w-6 text-primary" />
                <div className="text-center">
                  <div className="font-medium text-sm">Business Analytics</div>
                  <div className="text-xs text-muted-foreground">
                    Revenue, subscriptions, promotions
                  </div>
                </div>
              </Button>
              <Button
                variant="outline"
                className="h-24 flex flex-col items-center justify-center gap-2"
                onClick={() => navigate("/admin/analytics/system")}
              >
                <Eye className="h-6 w-6 text-primary" />
                <div className="text-center">
                  <div className="font-medium text-sm">System Analytics</div>
                  <div className="text-xs text-muted-foreground">
                    Performance, health, monitoring
                  </div>
                </div>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;
